import { Button, Flex, Grid, Image, ModalManagerContext, ModalMethods, Text } from '@pega/cosmos-react-core';
import React, { useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Logos from '../constants/images';
import useLocalTranslation from '../hooks/useLocalTranslation';
import { appState, customModalProps } from '../interfaces/common';
import { updateBannerInfo, updateEncryptionFlag, updateLoaderDetails } from '../redux/actions';
import { usePlatformVersionSelector } from '../redux/selectors';
import { authorizeFacebookPage } from './SocialMedia/Facebook/FacebookApis';
import AddConnectionCard from './SocialMedia/Home/AddConnectionCard';
import { CustomModal } from './Utilities/CustomModal';
import SectionRenderTemplate from './Utilities/SectionRenderTemplate';

const AddConnection: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const translate = useLocalTranslation();
  const { fullVersion, majorVersion, minorVersion, patchVersion } = usePlatformVersionSelector();
  const beyondEightDotSeven =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 7));
  const beyondEightDotSevenDotTwo = beyondEightDotSeven && patchVersion && patchVersion >= 2;
  const beyondEightDotEight =
    majorVersion && (majorVersion > 8 || (majorVersion === 8 && minorVersion && minorVersion >= 8));
  const isEightDotEight = fullVersion && fullVersion === 8.8 && !(patchVersion && patchVersion > 0);
  const isTwentyFiveOneOrHigher = majorVersion && majorVersion >= 25 && minorVersion && minorVersion >= 1;
  const { create } = useContext(ModalManagerContext);
  const modalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const enableVonage = process.env.REACT_APP_ENABLE_VONAGE === '1';
  const smsDesc = enableVonage ? `${translate('add_sms_description_1')}` : `${translate('add_sms_description')}`;
  const whatsAppDesc = enableVonage
    ? `${translate('add_whatsapp_description_1')}`
    : `${translate('add_whatsapp_description')}`;

  const { isClientPublickeyPresent, enableAgentTrainer, sessionId } = useSelector((state: appState) => state);
  const isFbTestSession = sessionId === 'dms_fb_test';
  const onFacebookClick = async () => {
    dispatch(updateLoaderDetails(true));
    const errorResponse = await authorizeFacebookPage(sessionId);
    if (errorResponse) {
      dispatch(updateLoaderDetails(false));
      dispatch(updateBannerInfo(translate('failed_to_authorize_facebook'), 'urgent'));
    }
  };

  const onTwilioWhatsappClick = () => {
    navigate('/twilio-whatsapp-create');
  };

  const onSMSClick = () => {
    navigate('/twilio-create');
  };

  const routeForChat = (route: string) => {
    if (beyondEightDotEight && isClientPublickeyPresent) {
      e2eeConfirmation(route);
    } else {
      navigate(route);
    }
  };

  const onChatClick = (platform?: string) => {
    const route = platform ? `/mobile/${platform}` : '/chat';
    routeForChat(route);
  };

  const onCombinedChatClick = (platform?: string) => {
    const route = `/unified-channel/${platform}`;
    routeForChat(route);
  };

  const e2eeConfirmation = async (route: string) => {
    modalMethods.current = create<customModalProps>(CustomModal, {
      heading: translate('e2ee_confirm_heading'),
      children: (
        <Flex container={{ direction: 'column', itemGap: 1 }}>
          <Text>{translate('e2ee_description_1')}</Text>
          {isEightDotEight && (
            <>
              <Text>{translate('e2ee_description_2')}</Text>
              <Text>{translate('e2ee_description_3')}</Text>
            </>
          )}
          <Text>{translate('e2ee_description_4')}</Text>
        </Flex>
      ),
      actions: (
        <>
          <Button
            onClick={async () => {
              await dispatch(updateEncryptionFlag(false));
              modalMethods.current?.dismiss();
              navigate(route);
            }}
          >
            {translate('standard_encryption')}
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              await dispatch(updateEncryptionFlag(true));
              modalMethods.current?.dismiss();
              navigate(route);
            }}
          >
            {translate('byok_encryption')}
          </Button>
        </>
      ),
    });
  };

  const onThirdPartyApiClick = () => {
    navigate('/third-party-connection-create');
  };

  const onCustomerAISimulatorClick = () => {
    navigate('/customer-ai-simulator-create');
  };

  const Facebook = {
    image: <Image src={Logos.FacebookLogo} alt="Facebook" />,
    title: `${translate('Facebook')}`,
    description: `${translate('add_fb_description')}`,
  };
  const SMS = {
    image: <Image src={Logos.SMSLogo} alt="SMS" style={{ position: 'relative', left: '10px' }} />,
    title: `${translate('SMS')}`,
    description: smsDesc,
    width: '84px',
    height: '101px',
  };
  const WhatsApp = {
    image: <Image src={Logos.WhatsappLogo} alt="WhatsApp" />,
    title: `${translate('WhatsApp')}`,
    description: whatsAppDesc,
  };
  const Chat = {
    image: <Image src={Logos.ChatLogo} alt="Chat" />,
    title: `${translate('Web Messaging')}`,
    description: `${translate('add_chat_description')}`,
  };
  const MobileSDK = {
    image: <Image src={Logos.SDKLogo} alt="Sdk" />,
    title: `${translate('Mobile Messaging SDK')}`,
    description: `${translate('add_sdk_description')}`,
  };
  const UnifiedMessagingChannel = {
    image: (
      <Image src={Logos.CombinedMessagingLogo} alt="Combined Messaging Channel" style={{ objectFit: 'contain' }} />
    ),
    title: `${translate('combined_messaging')}`,
    description: `${translate('add_combined_messaging_description')}`,
  };
  const ThirdPartyApi = {
    image: <Image src={Logos.CustomChannelLogo} alt="ThirdPartyChannel" />,
    title: `${translate('Client Channel API')}`,
    description: `${translate('add_api_connection')}`,
  };
  const AISimulator = {
    image: <Image src={Logos.CustomerSimulatorLogo} alt={translate('GenAI Agent Trainer')} />,
    title: `${translate('GenAI Agent Trainer')}`,
    description: `${translate('add_genAI_agent_trainer')}`,
  };

  const headerElement = () => {
    return (
      <Text variant="h1" as="h1">
        {translate('add_a_connection_label')}
      </Text>
    );
  };

  return (
    <SectionRenderTemplate header={headerElement()}>
      <Grid
        container={{
          cols: 'repeat(4, 1fr)',
          gap: 2,
        }}
        xs={{ container: { cols: '1fr' } }}
        sm={{ container: { cols: 'repeat(2, 1fr)' } }}
        md={{ container: { cols: beyondEightDotSeven ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' } }}
        lg={{ container: { cols: 'repeat(4, 1fr)' } }}
        xl={{ container: { cols: 'repeat(5, 1fr)' } }}
      >
        <AddConnectionCard onClick={onFacebookClick}>{Facebook}</AddConnectionCard>
        {!isFbTestSession && (
          <>
            <AddConnectionCard onClick={onSMSClick}>{SMS}</AddConnectionCard>
            <AddConnectionCard onClick={onTwilioWhatsappClick}>{WhatsApp}</AddConnectionCard>
            <AddConnectionCard onClick={() => onChatClick()}>{Chat}</AddConnectionCard>
            {beyondEightDotSevenDotTwo || beyondEightDotEight ? (
              <AddConnectionCard onClick={() => onChatClick('sdk_mobile')}>{MobileSDK}</AddConnectionCard>
            ) : (
              <></>
            )}
            {isTwentyFiveOneOrHigher && (
              <AddConnectionCard onClick={() => onCombinedChatClick('combined_messaging')}>
                {UnifiedMessagingChannel}
              </AddConnectionCard>
            )}
            {beyondEightDotSeven && (
              <AddConnectionCard onClick={onThirdPartyApiClick}>{ThirdPartyApi}</AddConnectionCard>
            )}
            {beyondEightDotSeven && enableAgentTrainer && (
              <AddConnectionCard onClick={onCustomerAISimulatorClick}>{AISimulator}</AddConnectionCard>
            )}
          </>
        )}
      </Grid>
    </SectionRenderTemplate>
  );
};

export default AddConnection;
